/**
 * Auth Service — aligned with AUTH_V2_REACT_FRONTEND_DOCS.md
 *
 * Handles: login attempt tokens, OTP, login/register, profile selection,
 * profile switching, token refresh, and logout.
 */
import axios from "axios";
import axiosInstance from "@/config/axiosInstance";
import { getAuthApiRoute } from "@/helpers/api.util";
import {
    clearAuthTokens,
    getAuthTokens,
    setAuthTokens,
} from "@/helpers/auth/tokenStorage";

// ---------------------------------------------------------------------------
// Login Attempt Token
// ---------------------------------------------------------------------------

/**
 * Generate a login attempt token that binds the auth flow to a specific
 * user-agent and IP. Must be called before sendLoginOTP.
 *
 * POST /generate-login-token
 * Rate Limit: 10 req / 60s
 *
 * @returns {{ token: string, expiresAt: number }}
 */
export const generateLoginToken = async () => {
    const { data } = await axiosInstance.post(
        getAuthApiRoute("generate-login-token"),
        {
            userAgent:
                typeof navigator === "undefined" ? "Manabakh-Web" : navigator.userAgent,
        },
        { skipAuth: true, skipAuthRefresh: true }
    );
    return data.data; // { token, expiresAt }
};

// ---------------------------------------------------------------------------
// OTP
// ---------------------------------------------------------------------------

/**
 * Send a one-time password to the given mobile number.
 *
 * POST /send-login-otp
 * Rate Limit: 2 req / 60s
 *
 * @param {{ mobileNumber: number, loginAttemptToken?: string, appSignature?: string }} params
 */
export const sendLoginOTP = async (params) => {
    const { data } = await axiosInstance.post(
        getAuthApiRoute("send-login-otp"),
        params,
        { skipAuth: true, skipAuthRefresh: true }
    );
    return data;
};

// ---------------------------------------------------------------------------
// Login / Register
// ---------------------------------------------------------------------------

/**
 * Login or register with OTP or password. Returns **temporary** tokens
 * that must be upgraded via selectProfile().
 *
 * POST /login-register
 *
 * @param {{ mobileNumber: number, otp?: number, password?: string, loginAttemptToken?: string }} params
 * @returns {{ accessToken: string, refreshToken: string }}
 */
export const loginRegister = async (params) => {
    const { data } = await axiosInstance.post(
        getAuthApiRoute("login-register"),
        params,
        { skipAuth: true, skipAuthRefresh: true }
    );
    return data?.data; // { accessToken, refreshToken }
};

// ---------------------------------------------------------------------------
// Profile Selection & Switching
// ---------------------------------------------------------------------------

/**
 * Select a profile to complete the login flow. Returns **final** session tokens.
 *
 * POST /select-profile  (Bearer: temporary token)
 *
 * @param {{ profileId: string, loginAttemptToken?: string }} params
 * @param {string} tempToken — the temporary access token from loginRegister
 * @returns {{ accessToken: string, refreshToken: string, profile: object }}
 */
export const selectProfile = async (params, tempToken) => {
    const { data } = await axiosInstance.post(
        getAuthApiRoute("select-profile"),
        params,
        {
            headers: { Authorization: `Bearer ${tempToken}` },
            skipAuthRefresh: true,
        }
    );
    const result = data.data;

    if (!result?.accessToken || !result?.refreshToken) {
        throw new Error("Profile selection did not return a complete token pair");
    }

    setAuthTokens(result.accessToken, result.refreshToken);
    return result; // { accessToken, refreshToken, profile }
};

/**
 * Switch the active profile within an existing session.
 * Old tokens are **invalidated** — store the new ones immediately.
 *
 * POST /switch-profile  (Bearer)
 *
 * @param {string} profileId
 * @returns {{ accessToken: string, refreshToken: string }}
 */
export const switchProfile = async (profileId) => {
    const { data } = await axiosInstance.post(
        getAuthApiRoute("switch-profile"),
        { profileId }
    );
    const result = data.data;

    if (!result?.accessToken) {
        throw new Error("Profile switch did not return an access token");
    }

    // Switching invalidates the old pair, so persist the replacement before
    // returning control to React.
    const { refreshToken: currentRefreshToken } = getAuthTokens();
    setAuthTokens(
        result.accessToken,
        result.refreshToken || currentRefreshToken
    );
    return result;
};

// ---------------------------------------------------------------------------
// Token Management
// ---------------------------------------------------------------------------

/**
 * Refresh the access token using a refresh token.
 * Uses a plain axios instance to avoid interceptor loops.
 *
 * POST /refresh-token
 *
 * @param {string} token — the current refresh token
 * @returns {{ accessToken: string, refreshToken: string }}
 */
export const refreshToken = async (token) => {
    const { data } = await axios.post(getAuthApiRoute("refresh-token"), {
        refreshToken: token,
    });
    const result = data?.data ?? data;

    if (!result?.accessToken) {
        throw new Error("Token refresh did not return an access token");
    }

    setAuthTokens(result.accessToken, result.refreshToken || token);
    return result;
};

// ---------------------------------------------------------------------------
// Logout
// ---------------------------------------------------------------------------

/**
 * Logout the current session.
 *
 * POST /logout  (Bearer)
 */
export const logout = async () => {
    try {
        await axiosInstance.post(getAuthApiRoute("logout"), undefined, {
            skipAuthRefresh: true,
        });
    } finally {
        clearAuthTokens();
    }
};
